import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FiRefreshCw, FiSend, FiZap, FiLock, FiX, FiPaperclip, FiFileText, FiFolder } from 'react-icons/fi';
import '../../styles/ChannelChat.css';
import '../../styles/AmpChat.css';
import '../../styles/ChatMessage.css';
import '../../styles/MomentumClaw.css';
import { sendClawMessage, uploadFileToVM } from '../../services/momentumClawService';
import { uploadAmsFiles, AmsAttachmentRef } from '../../services/fileUploadService';
import SelectFromAmsDialog, { AmsUploadSelection } from '../momentumcommand/SelectFromAmsDialog';
import { useTheme } from '../../context/ThemeContext';
import ThinkingBlock from '../chat/ThinkingBlock';
import logo from '../../assets/images/M_Multicolor.svg';
import fileIcon from '../../assets/images/FileIcon.png';
import '../../styles/ChatInput.css';
import ProceedQuickReplies from '../../components/ui/ProceedQuickReplies';
import { asksToProceed } from '../../utils/proceedPrompt';

function processThought(thought: string): string | null {
  if (!thought.startsWith('[VM]')) return thought || null;

  const inner = thought.replace(/^\[VM\]\s*/, '');

  const cmdMatch = inner.match(/^root@[^:]+:[^#]*#\s*(.+)/);
  if (cmdMatch) {
    const cmd = cmdMatch[1].trim();
    if (cmd.startsWith("echo '") && cmd.length > 40) return null;
    return `$ ${cmd}`;
  }

  if (inner.startsWith('data: ')) {
    try {
      const json = JSON.parse(inner.slice(6));
      if (json.type === 'text' || json.type === 'done') return null;
      if (json.type === 'lifecycle') return json.phase === 'start' ? '⚡ Agent started' : null;
      if (json.type === 'thinking') return null;
    } catch { /* ignore parse errors */ }
    return null;
  }

  return null;
}

interface Message {
  id: string | number;
  role: 'user' | 'assistant';
  content: string;
  thoughts?: string[];
  streaming?: boolean;
  attachmentName?: string;
}

const SUGGESTIONS = [
  { icon: <FiZap size={14} />, color: '#8b5cf6', text: 'What is Momentum Claw?' },
  { icon: <FiRefreshCw size={14} />, color: '#22d3ee', text: 'Which carriers are supported?' },
  { icon: <FiRefreshCw size={14} />, color: '#f59e0b', text: 'How does AMS sync work?' },
  { icon: <FiLock size={14} />, color: '#34d399', text: 'Is my data secure?' },
];

function formatTime(): string {
  const now = new Date();
  let h = now.getHours();
  const m = now.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
}

// Reads localStorage fresh on every call (not cached at module load) so a
// logout/login as a different user without a page reload shows the right
// initials instead of the previous user's.
function getUserInitials(): string {
  const f = localStorage.getItem('firstName') ?? '';
  const l = localStorage.getItem('lastName') ?? '';
  const first = f.charAt(0).toUpperCase();
  const last = l.charAt(0).toUpperCase();
  return first + last || first || (localStorage.getItem('userName') ?? '?').charAt(0).toUpperCase();
}

let msgCounter = 0;


interface MomentumClawChatProps {
  conversationId: string;
}

const MomentumClawChat: React.FC<MomentumClawChatProps> = ({ conversationId }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [videoOpen, setVideoOpen] = useState(false);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [attachedAmsFiles, setAttachedAmsFiles] = useState<AmsAttachmentRef[]>([]);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [showAmsSelector, setShowAmsSelector] = useState(false);
  const [amsUploading, setAmsUploading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachMenuRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();

  useEffect(() => {
    setMessages([]);
    setInputValue('');
    setLoading(false);
    setAttachedFile(null);
    setAttachedAmsFiles([]);
  }, [conversationId]);

  // Close the attach menu on outside click
  useEffect(() => {
    if (!attachMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target as Node)) {
        setAttachMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [attachMenuOpen]);

  // AMS-drive selections are uploaded server-side to blob storage (the AMS
  // pre-signed download URLs are not CORS-enabled, so the browser cannot fetch
  // the bytes itself). The VM agent then downloads each file from its blob URL
  // with its own tools. Errors propagate so the AMS dialog stays open and shows
  // them.
  const handleAmsFilesUpload = useCallback(async (selections: AmsUploadSelection[]) => {
    if (!selections.length) return;
    setAmsUploading(true);
    try {
      const refs = await uploadAmsFiles(selections, conversationId);
      setAttachedAmsFiles(prev => [...prev, ...refs]);
    } finally {
      setAmsUploading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const handleSend = useCallback(async (question: string, file?: File | null) => {
    const hasText = question.trim().length > 0;
    const amsFiles = attachedAmsFiles;
    if (!hasText && !file && amsFiles.length === 0) return;
    if (loading) return;

    const displayText = hasText ? question.trim() : 'Please analyze the attached file.';
    const attachmentName = [file?.name, ...amsFiles.map(a => a.name)].filter(Boolean).join(', ') || undefined;
    const userId = `user-${++msgCounter}`;
    setMessages(prev => [...prev, { id: userId, role: 'user', content: displayText, attachmentName }]);
    setInputValue('');
    setAttachedFile(null);
    setAttachedAmsFiles([]);
    setLoading(true);

    // Upload file first, then build context to prepend to the message
    let fileContext = '';
    if (file) {
      const result = await uploadFileToVM(file);
      if (result?.ok) {
        const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp'].includes(ext);
        if (isImage) {
          fileContext = `[The user has uploaded an image file at ${result.filePath} (${file.name}, ${(result.fileSize / 1024).toFixed(1)}KB). Use the bash tool to run: base64 ${result.filePath} — then include the base64 content as an image in your analysis. Or read the file directly to describe it.]\n\n`;
        } else {
          fileContext = `[The user has uploaded a file at ${result.filePath} (${file.name}, ${(result.fileSize / 1024).toFixed(1)}KB). Read the file contents using your tools to analyze it.]\n\n`;
        }
      }
    }
    // AMS-drive files were already copied to blob storage — have the VM fetch them
    for (const a of amsFiles) {
      fileContext += `[The user has attached "${a.name}" from their AMS drive. Use the bash tool to download it: curl -L -o "/tmp/${a.name}" '${a.url}' — then read the downloaded file to analyze it.]\n\n`;
    }

    const finalQuestion = fileContext + displayText;
    const msgId = Date.now();
    setMessages(prev => [...prev, { id: msgId, role: 'assistant', content: '', thoughts: [], streaming: true }]);

    await sendClawMessage(
      finalQuestion,
      conversationId,
      (chunk) => {
        setMessages(prev =>
          prev.map(msg => msg.id === msgId ? { ...msg, content: msg.content + chunk } : msg)
        );
      },
      () => {
        setMessages(prev =>
          prev.map(msg => msg.id === msgId ? { ...msg, streaming: false } : msg)
        );
        setLoading(false);
      },
      () => {
        setMessages(prev =>
          prev.map(msg => msg.id === msgId
            ? { ...msg, content: 'Something went wrong. Please try again.', streaming: false }
            : msg
          )
        );
        setLoading(false);
      },
      (thought) => {
        setMessages(prev =>
          prev.map(msg => msg.id === msgId
            ? { ...msg, thoughts: [...(msg.thoughts || []), thought] }
            : msg
          )
        );
      }
    );
  }, [loading, conversationId, attachedAmsFiles]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(inputValue, attachedFile);
    }
  }, [handleSend, inputValue, attachedFile]);

  return (
    <motion.div
      className="mc-chat-view"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="mc-chat-messages">
        {messages.length === 0 && !loading && (
          <div className="mc-chat-welcome">
            <motion.div className="mc-chat-welcome__icon-wrap"
              initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}>
              <div className="mc-chat-welcome__icon-ring" />
              <img src={logo} alt="" className="mc-chat-welcome__logo" />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.35 }}>
              <p className="mc-chat-welcome__title">MomentumClaw</p>
              <p className="mc-chat-welcome__sub">
                I automate data extraction from carrier portals and sync it directly into your AMS — eliminating manual entry, missed updates, and data lag. Ask me anything.
              </p>
            </motion.div>

            <motion.button
              className="mc-chat-welcome__play-btn"
              onClick={() => setVideoOpen(true)}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.35 }}
              whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
            >
              <span className="mc-chat-welcome__play-icon">
                <svg width="12" height="14" viewBox="0 0 12 14" fill="currentColor"><path d="M1 1l10 6-10 6V1z"/></svg>
              </span>
              Watch how it works
            </motion.button>

            <motion.div className="amp-welcome__hints"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}>
              <span className="amp-welcome__hint">AI-powered extraction</span>
              <span className="amp-welcome__hint">Direct AMS sync</span>
              <span className="amp-welcome__hint">Secure &amp; compliant</span>
            </motion.div>

            <div className="channel-prompts">
              {SUGGESTIONS.map((s, i) => (
                <motion.button
                  key={i}
                  className="channel-prompts__card glass-surface"
                  onClick={() => handleSend(s.text)}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.06, duration: 0.35 }}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="channel-prompts__icon" style={{ color: s.color }}>{s.icon}</span>
                  <span className="channel-prompts__prompt">{s.text}</span>
                </motion.button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, idx) => {
          const isUser = msg.role === 'user';
          const isStreaming = !!msg.streaming;
          // Yes / No replies only on the newest settled AI answer.
          const showProceedReplies =
            !isUser && !isStreaming && !loading && idx === messages.length - 1 && asksToProceed(msg.content);
          const hasThoughts = !isUser && !!msg.thoughts && msg.thoughts.length > 0;
          const showThinking = !isUser && isStreaming && !msg.content && !hasThoughts;

          return (
            <motion.div
              key={msg.id}
              className={`chat-msg chat-msg--${isUser ? 'user' : 'assistant'}${isStreaming ? ' chat-msg--streaming' : ''}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              <div className="chat-msg__header">
                <div className="chat-msg__avatar">
                  {isUser
                    ? <div className="chat-msg__avatar-user">{getUserInitials()}</div>
                    : <div className="chat-msg__avatar-ai">
                        <img src={logo} alt="MomentumClaw" className="chat-msg__avatar-ai-img" />
                      </div>
                  }
                </div>
                <span className="chat-msg__sender">{isUser ? 'You' : 'Momentum Claw'}</span>
                <span className="chat-msg__time">{formatTime()}</span>
              </div>

              <div className="chat-msg__body">
                {isUser && msg.attachmentName && (
                  <div className="mc-msg-attachment">
                    <FiPaperclip size={11} />
                    <span>{msg.attachmentName}</span>
                  </div>
                )}

                {hasThoughts && (
                  <ThinkingBlock
                    thoughtText={msg.thoughts!.map(processThought).filter(Boolean).join('\n')}
                    isActiveThinking={isStreaming && !msg.content}
                    hasAnswer={!!msg.content}
                  />
                )}

                {showThinking && (
                  <div className="mc-thinking">
                    <span /><span /><span />
                  </div>
                )}

                {msg.content && (
                  <div className="chat-msg__markdown">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                  </div>
                )}

                {showProceedReplies && (
                  <ProceedQuickReplies onReply={(text) => { void handleSend(text); }} />
                )}
              </div>
            </motion.div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="mc-chat-input-bar">
        {(attachedFile || attachedAmsFiles.length > 0 || amsUploading) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
            {attachedFile && (
              <div className="chat-input__file-chip glass-surface">
                <span className="chat-input__file-icon"><FiFileText size={16} /></span>
                <span className="chat-input__file-info">
                  <span className="chat-input__file-name">{attachedFile.name}</span>
                  <span className="chat-input__file-size">
                    {attachedFile.size < 1024 * 1024
                      ? `${(attachedFile.size / 1024).toFixed(1)} KB`
                      : `${(attachedFile.size / (1024 * 1024)).toFixed(1)} MB`}
                  </span>
                </span>
                <button className="chat-input__file-remove" type="button" onClick={() => setAttachedFile(null)}>
                  <FiX size={12} />
                </button>
              </div>
            )}
            {attachedAmsFiles.map((a, i) => (
              <div key={`${a.name}-${i}`} className="chat-input__file-chip glass-surface">
                <span className="chat-input__file-icon"><FiFolder size={16} /></span>
                <span className="chat-input__file-info">
                  <span className="chat-input__file-name">{a.name}</span>
                  <span className="chat-input__file-size">
                    {a.size
                      ? (a.size < 1024 * 1024
                        ? `${(a.size / 1024).toFixed(1)} KB`
                        : `${(a.size / (1024 * 1024)).toFixed(1)} MB`)
                      : 'From AMS'}
                  </span>
                </span>
                <button
                  className="chat-input__file-remove"
                  type="button"
                  onClick={() => setAttachedAmsFiles(prev => prev.filter((_, j) => j !== i))}
                >
                  <FiX size={12} />
                </button>
              </div>
            ))}
            {amsUploading && (
              <div className="chat-input__file-chip glass-surface">
                <span className="chat-input__file-icon"><FiRefreshCw size={16} className="mc-spin" /></span>
                <span className="chat-input__file-info">
                  <span className="chat-input__file-name">Fetching from AMS…</span>
                </span>
              </div>
            )}
          </div>
        )}
        <div className="mc-chat-input-wrap">
          <textarea
            ref={inputRef}
            className="mc-chat-input"
            placeholder="Ask about MomentumClaw…"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            disabled={loading}
          />
          <div ref={attachMenuRef} style={{ position: 'relative', display: 'flex' }}>
            <button
              className="chat-input__icon-btn"
              type="button"
              onClick={() => setAttachMenuOpen(v => !v)}
              disabled={loading}
              title="Attach file"
            >
              <img src={fileIcon} alt="Attach" style={{ width: 20, height: 19 }} />
            </button>
            {attachMenuOpen && (
              <div style={{ position: 'absolute', bottom: 'calc(100% + 6px)', right: 0, minWidth: 190, background: theme === 'dark' ? '#1e1e35' : '#ffffff', border: '1px solid var(--border-glass, rgba(255,255,255,0.09))', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', zIndex: 50, overflow: 'hidden' }}>
                <div style={{ padding: '8px 16px 4px', fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'Poppins, sans-serif' }}>Choose upload type:</div>
                <button
                  type="button"
                  onClick={() => { setAttachMenuOpen(false); fileInputRef.current?.click(); }}
                  style={{ width: '100%', padding: '9px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, fontFamily: 'Poppins, sans-serif', color: 'var(--text-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
                >
                  <FiPaperclip size={13} /> Upload file
                </button>
                <button
                  type="button"
                  onClick={() => { setAttachMenuOpen(false); setShowAmsSelector(true); }}
                  style={{ width: '100%', padding: '9px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, fontFamily: 'Poppins, sans-serif', color: 'var(--text-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
                >
                  <FiFolder size={13} /> Select from AMS
                </button>
              </div>
            )}
          </div>
          <button
            className="mc-chat-send"
            onClick={() => handleSend(inputValue, attachedFile)}
            disabled={(!inputValue.trim() && !attachedFile && attachedAmsFiles.length === 0) || loading || amsUploading}
          >
            {loading
              ? <FiRefreshCw size={16} className="mc-spin" />
              : <FiSend size={16} />
            }
          </button>
          <input
            type="file"
            ref={fileInputRef}
            className="mc-chat-file-input"
            onChange={e => { if (e.target.files?.[0]) { setAttachedFile(e.target.files[0]); e.target.value = ''; } }}
          />
        </div>
      </div>

      {/* Select from AMS Drive — browse a client's AMS files and attach them to this chat */}
      <SelectFromAmsDialog
        visible={showAmsSelector}
        onClose={() => setShowAmsSelector(false)}
        onUpload={handleAmsFilesUpload}
      />

      {videoOpen && (
        <motion.div
          className="mc-video-backdrop"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setVideoOpen(false)}
        >
          <motion.div
            className="mc-video-modal"
            initial={{ opacity: 0, scale: 0.93, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.93, y: 24 }}
            transition={{ duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mc-video-modal__header">
              <span className="mc-video-modal__title">How MomentumClaw Works</span>
              <button className="mc-video-modal__close" onClick={() => setVideoOpen(false)}>
                <FiX size={18} />
              </button>
            </div>
            <div className="mc-video-modal__frame-wrap">
              <iframe
                src="https://www.youtube.com/embed/LDtFmXKGzGg?start=2&autoplay=1"
                title="MomentumClaw Demo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="mc-video-modal__iframe"
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default MomentumClawChat;
